import { useEffect, useRef } from "react";
import {
  useApiGet,
  useApiMutate,
  useQueryClient,
} from "@skanska/vsa-client-auth";
import { ApiEndpoints } from "../models/ApiEndpoints";
import { ApiMethods } from "../models/ApiMethods";
import {
  ImageFileMigrationJob,
  ImageFileMigrationStartRequest,
} from "../models/ImageFileMigration";

const queryKey = "image-file-migration";

function useMigrationMutationErrorHandling() {
  const queryClient = useQueryClient();
  queryClient.setMutationDefaults([queryKey], { throwOnError: false });
}

export function useImageFileMigration(jobId?: string) {
  const queryClient = useQueryClient();
  const startedPollingAt = useRef(0);
  const errorCount = useRef(0);
  const result = useApiGet<ImageFileMigrationJob>(
    ApiEndpoints.ImageFileMigrations,
    jobId ?? null,
    [queryKey, jobId],
    { enabled: Boolean(jobId) }
  );

  useEffect(() => {
    startedPollingAt.current = 0;
    errorCount.current = 0;
  }, [jobId]);

  useEffect(() => {
    if (
      !jobId ||
      document.hidden ||
      !navigator.onLine ||
      result.error?.status === 404
    )
      return;
    if (startedPollingAt.current === 0) startedPollingAt.current = Date.now();
    if (result.data && [2, 3, 4, 5].includes(result.data.status)) return;
    errorCount.current = result.error ? errorCount.current + 1 : 0;
    const elapsed = Date.now() - startedPollingAt.current;
    const delay = result.error
      ? [10_000, 20_000, 30_000][Math.min(errorCount.current - 1, 2)]
      : elapsed < 500
        ? 500
        : elapsed < 10_000
          ? 2_000
          : 5_000;
    const timer = window.setTimeout(
      () => queryClient.invalidateQueries({ queryKey: [queryKey, jobId] }),
      delay
    );
    return () => window.clearTimeout(timer);
  }, [jobId, queryClient, result.data, result.error]);

  useEffect(() => {
    const refresh = () => {
      if (
        !document.hidden &&
        navigator.onLine &&
        jobId &&
        result.error?.status !== 404
      ) {
        queryClient.invalidateQueries({ queryKey: [queryKey, jobId] });
      }
    };
    window.addEventListener("focus", refresh);
    window.addEventListener("online", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("online", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [jobId, queryClient, result.error]);

  return result;
}

export function useStartImageFileMigration() {
  useMigrationMutationErrorHandling();
  return useApiMutate<ImageFileMigrationJob, ImageFileMigrationStartRequest>(
    ApiEndpoints.ImageFileMigrations,
    ApiMethods.Post,
    null,
    null,
    [queryKey]
  );
}

export function useCancelImageFileMigration(jobId?: string) {
  useMigrationMutationErrorHandling();
  return useApiMutate<ImageFileMigrationJob, void>(
    `${ApiEndpoints.ImageFileMigrations}/${jobId}/cancel`,
    ApiMethods.Post,
    null,
    null,
    [queryKey]
  );
}
